import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { RadioButton } from "primereact/radiobutton";
import { InputTextarea } from "primereact/inputtextarea";
import {
  Label,
  InputText,
  Dropdown,
  MultiSelect,
} from "../../../components/Inputs";
import RichTextEditor from "../../../components/RichTextEditor";
import HtmlText from "../../../components/HtmlText";

const getTextPreviewHtml = (textPreview) =>
  `<p><strong style="color: rgb(230, 0, 0);">${textPreview}</strong></p>`;

const getStateModel = (model = {}, props) => {
  let footPrints = props.footPrints;
  let footPrintOptions = props.footPrintOptions;
  if (props.titlePrefixOptions.length > 0) {
    footPrints = props.footPrintGroups[0] || footPrints;
    footPrintOptions = props.footPrintGroupOptions[0] || footPrintOptions;
  }
  return {
    // roles: model.roles || [],
    // companyGroups: model.companyGroups || [],
    medium: model.medium || "email",
    footPrint: "",
    textPreview: "",
    titlePrefix: "",
    title: "",
    text: "<p><br/></p>",
    attachments: model.attachments,
    footPrints,
    footPrintOptions,
  };
};

const getState = (props) => {
  const newModel = getStateModel(props.model, props);
  return {
    ...newModel,
    error: {
      medium: null,
      titlePrefix: null,
      footPrint: null,
      title: null,
      text: null,
    },
  };
};

function MessageForm(props) {
  const {
    forwardedRef,
    className,
    titlePrefixOptions,
    // companyGroupOptions,
    footPrintGroups,
    footPrintGroupOptions,
    readOnly,
    model,
    signature,
    onUpdated,
  } = props;
  const [state, setState] = React.useState(getState(props));
  const onChange = React.useCallback((key, value) => {
    setState((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const validate = React.useCallback(
    (noValidate, noUpdate) => {
      const { error, footPrints, footPrintOptions, ...newModel } = state;
      const listValues = [];
      // check if all values are set
      let isValid = true;
      Object.keys(newModel).forEach((key) => {
        if (!noValidate && error[key] !== undefined) {
          const test = listValues.includes(key)
            ? newModel[key].length === 0
            : newModel[key] === "" || newModel[key] === null;
          if (test) {
            error[key] = true;
            isValid = false;
          } else {
            error[key] = null;
          }
        }
      });
      if (!isValid) {
        if (!noUpdate) {
          setState((prev) => ({ ...prev, error }));
        }
        return null;
      }
      // Add greeting
      newModel.text =
        `<div><p>Sehr geehrte Damen und Herren,</p></div>` + newModel.text;
      return newModel;
    },
    [state],
  );

  React.useEffect(() => {
    if (!readOnly) {
      const newModel = validate(false, true);
      onUpdated && onUpdated(newModel !== null);
    }
  }, [state, onUpdated]);

  const getModel = React.useCallback(
    (noValidate, noUpdate) => {
      return validate(noValidate, noUpdate);
    },
    [validate],
  );
  const reset = React.useCallback(() => {
    setState(getState(props));
  }, [state, props]);
  React.useImperativeHandle(forwardedRef, () => ({
    getModel,
    reset,
  }));

  const setFootPrint = React.useCallback(
    (footPrint) => {
      const { footPrints, footPrintOptions } = state;
      const textPreview = footPrints[footPrint] || "";
      let text = state.text;
      const newTextPreview = getTextPreviewHtml(textPreview);
      let oldTextPreview = null;
      for (const option of footPrintOptions) {
        const value = getTextPreviewHtml(footPrints[option.value]);
        const reformattedText = text.replace("&amp;", "&");
        // console.log(`>> >> searching for "${value}" in "${reformattedText}"...`);
        if (reformattedText.indexOf(value) === 0) {
          oldTextPreview = value;
          break;
        }
      }
      let subString = oldTextPreview
        ? text.substring(oldTextPreview.length)
        : text;
      if (subString.length === 0) {
        subString = "<p><br/></p>";
      }
      console.log("setFootPrint()", newTextPreview, subString);
      text = `${newTextPreview}${subString}`;
      setState((prev) => ({
        ...prev,
        footPrint,
        textPreview,
        text,
      }));
    },
    [state],
  );

  const setTitlePrefix = React.useCallback(
    (titlePrefix) => {
      const index = titlePrefixOptions.findIndex(
        (item) => item.value === titlePrefix,
      );
      if (index >= 0) {
        setState((prev) => ({
          ...prev,
          titlePrefix,
          footPrints: footPrintGroups[index],
          footPrintOptions: footPrintGroupOptions[index],
        }));
        setFootPrint("");
      }
    },
    [titlePrefixOptions, footPrintGroups, footPrintGroupOptions, setFootPrint],
  );

  // const source = (readOnly && model) ? model : state;
  const {
    medium,
    text,
    // roles,
    // companyGroups,
    footPrint,
    textPreview,
    titlePrefix,
    title,
  } = state; // source;

  let textMarkup = <HtmlText className="w3-border pad-big" text={text} />;
  if (!readOnly) {
    textMarkup =
      medium === "sms" ? (
        <InputTextarea
          id="text"
          className="w3-block w3-border"
          value={text}
          autoResize
          onChange={(event) => onChange("text", event.target.value)}
          readOnly={readOnly}
        />
      ) : (
        <div className="clearfix pad-big-lr w3-light-grey">
          <div>
            <p>{"Sehr geehrte Damen und Herren,"}</p>
          </div>
          <RichTextEditor
            id="text"
            className="w3-white"
            value={text}
            onChange={(event) => onChange("text", event.target.value)}
          />
          <div className="pad-big-btm">
            <HtmlText text={signature} />
          </div>
        </div>
      );
  }

  return (
    <div className={`${className} pos-rel`}>
      <div className="neg-margin-lr">
        {/* <div className="w3-row">
          <div className={`w3-col s4 pad-lr form-group-item pad-top`}>
            <Label htmlFor="roles">{`Aufgabengebiet`}</Label>
            {readOnly && (
              <InputText
                className="w3-block w3-border"
                value={roles.map((role) => role.name).join(", ")}
                readOnly
                style={{
                  padding: "0px 6px",
                  height: "37.34px",
                  lineHeight: "100%",
                }}
              />
            )}
            {!readOnly && (
              <MultiSelect
                id="roles"
                optionLabel="label"
                value={roles}
                placeholder="Bitte auswählen"
                options={props.roles}
                onChange={(event) => onChange("roles", event.value)}
                selectedItemsLabel="{0} Rollen ausgewählt"
                filter
                appendTo={document.body}
              />
            )}
          </div>
          <div className={`w3-col s4 pad-lr form-group-item pad-top`}>
            <Label htmlFor="roles">{`Vertragspartner`}</Label>
            {readOnly && (
              <InputText
                className="w3-block w3-border"
                value={companyGroups.join(", ")}
                readOnly
                style={{
                  padding: "0px 6px",
                  height: "37.34px",
                  lineHeight: "100%",
                }}
              />
            )}
            {!readOnly && (
              <MultiSelect
                id="roles"
                optionLabel="label"
                value={companyGroups}
                placeholder="Bitte auswählen"
                options={companyGroupOptions}
                onChange={(event) => onChange("companyGroups", event.value)}
                selectedItemsLabel="{0} Firmengruppen ausgewählt"
                filter
                appendTo={document.body}
              />
            )}
          </div>
        </div> */}
        <div className="w3-row">
          <div className="w3-col s4 pad-lr form-group-item pad-top pad-sm-btm">
            <Label htmlFor="titlePrefix">{`Betreff`}</Label>
            <Dropdown
              id="titlePrefix"
              className="w3-block w3-border"
              value={titlePrefix}
              options={titlePrefixOptions}
              onChange={(event) => setTitlePrefix(event.value)}
              placeholder="Bitte auswählen"
              // showClear
              readOnly={readOnly}
            />
          </div>
          <div className="w3-col s8 pad-lr form-group-item pad-top pad-sm-btm">
            <Label htmlFor="title">&nbsp;</Label>
            <InputText
              id="title"
              className="w3-block w3-border"
              value={title}
              onChange={(event) => onChange("title", event.target.value)}
              placeholder="Frei definierbarer Betreff"
              readOnly={readOnly}
              style={{
                padding: "0px 6px",
                height: "40px",
                lineHeight: "100%",
              }}
            />
          </div>
        </div>
        <div className="w3-row">
          <div className="w3-col s4 pad-lr form-group-item pad-top pad-sm-btm">
            <Label htmlFor="footprint">{`VF Footprint`}</Label>
            <Dropdown
              id="footPrint"
              className="w3-border w3-round"
              value={footPrint}
              placeholder="Bitte auswählen"
              options={state.footPrintOptions}
              onChange={(event) => setFootPrint(event.value)}
              showClear
              readOnly={readOnly}
            />
          </div>
          <div className="w3-col s8 pad-lr form-group-item pad-top pad-sm-btm">
            <Label htmlFor="textPreview">{`Text Voransicht`}</Label>
            <div
              className="w3-block w3-border w3-round"
              style={{
                display: "flex",
                alignItems: "center",
                padding: "0px 6px",
                height: "40px",
                lineHeight: "100%",
              }}
            >{textPreview}</div>
          </div>
        </div>
        <div className="w3-row pad-big-top">
          <div className="w3-col s12 pad-lr">
            <Label htmlFor="text">{`Ad-Hoc News Inhalt`}</Label>
            {textMarkup}
          </div>
        </div>
      </div>
    </div>
  );
}

MessageForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.shape({
    // roles: PropTypes.array.isRequired,
    // companyGroups: PropTypes.array.isRequired,
    medium: PropTypes.string.isRequired,
    footPrint: PropTypes.string.isRequired,
    textPreview: PropTypes.string.isRequired,
    titlePrefix: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    attachments: PropTypes.array,
  }),
  roles: PropTypes.array,
  footPrints: PropTypes.shape({}),
  footPrintOptions: PropTypes.arrayOf(PropTypes.shape({})),
  footPrintGroups: PropTypes.arrayOf(PropTypes.shape({})),
  footPrintGroupOptions: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.shape({})),
  ),
  titlePrefixOptions: PropTypes.array,
  companyGroupOptions: PropTypes.array,
  signature: PropTypes.string.isRequired,
  onUpdated: PropTypes.func,
  // getContractGivers: PropTypes.func.isRequired,
  // getContractors: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

MessageForm.defaultProps = {
  className: "",
  model: {
    // roles: [],
    // companyGroups: [],
    medium: "email",
    footPrint: "",
    textPreview: "",
    titlePrefix: `${moment().year()}-<Anzahl> ad-hoc News`,
    title: "",
    text: "",
    attachments: [],
  },
  // roles: [],
  // companyList: [],
  footPrints: {},
  footPrintOptions: [],
  footPrintGroups: [{}],
  footPrintGroupOptions: [[]],
  titlePrefixOptions: [],
  companyGroupOptions: [],
  signature: "",
};

export default React.forwardRef((props, ref) => (
  <MessageForm {...props} forwardedRef={ref} />
));
